/** ラベルの CRUD 操作を提供するフック群 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { labelsApi } from "../lib/api";
import type { Label } from "../types/label";

const LABELS_KEY = ["labels"] as const;

export const useLabels = () => {
  return useQuery({
    queryKey: LABELS_KEY,
    queryFn: async (): Promise<Label[]> => {
      const res = await labelsApi.index.$get();
      if (!res.ok) throw new Error("Failed to fetch labels");
      const json = await res.json();
      return json.data;
    },
  });
};

export const useCreateLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await labelsApi.index.$post({ json: data });
      if (!res.ok) throw new Error("Failed to create label");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    },
    onError: (err) => {
      toast.error("ラベルの追加に失敗しました");
    },
  });
};

export const useUpdateLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: { id: string; data: { name: string } }) => {
      const res = await labelsApi[":id"].$patch({
        param: { id },
        json: data,
      });
      if (!res.ok) throw new Error("Failed to update label");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    },
    onError: (err) => {
      toast.error("ラベルの更新に失敗しました");
    },
  });
};

export const useDeleteLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await labelsApi[":id"].$delete({ param: { id } });
      if (!res.ok) throw new Error("Failed to delete label");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      queryClient.invalidateQueries({ queryKey: ["userSettings"] });
    },
    onError: (err) => {
      toast.error("ラベルの削除に失敗しました");
    },
  });
};
